import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalDashboardFilters } from '@/pages/dashboard/dashboardFilters';
import { isDateRange, isSelect, type FilterConfig, type FilterOption } from '@/v5/types/filters';
import type { ReplyStatus, ReviewQueryParams, ReviewSort, Sentiment } from '@/services/googleBusiness.service';

/**
 * Filter, sort and page state for the Reviews screen.
 *
 * Lives outside the tab because the filter RAIL is rendered by the page — it is
 * a page-level panel beside the main navigation, the same position Patients uses
 * — while the results are rendered by the tab. Two components, one piece of
 * state, so it belongs to neither and sits here.
 *
 * None of this is Google's doing: its reviews endpoint accepts only pageSize, an
 * opaque pageToken and a three-value orderBy, and cannot filter at all. Every
 * option below is applied by our own backend over a set it fetched once.
 */

export const PAGE_SIZE = 10;

/**
 * Rating and sentiment are checklists because they genuinely combine — "1 and 2
 * stars" is an ordinary request. Reply status is single-valued: selecting both
 * of its options would mean every review, which is what clearing it already
 * does.
 */
export const REVIEW_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'rating', label: 'Rating', type: 'select', multi: true },
  { key: 'sentiment', label: 'Sentiment', type: 'select', multi: true },
  { key: 'replyStatus', label: 'Reply status', type: 'select' },
  { key: 'date', label: 'Review date', type: 'daterange' },
];

export const REVIEW_FILTER_OPTIONS: Record<string, FilterOption[]> = {
  rating: [
    { value: '5', label: '5 stars' },
    { value: '4', label: '4 stars' },
    { value: '3', label: '3 stars' },
    { value: '2', label: '2 stars' },
    { value: '1', label: '1 star' },
  ],
  sentiment: [
    { value: 'positive', label: 'Positive (4-5)' },
    { value: 'neutral', label: 'Neutral (3)' },
    { value: 'negative', label: 'Negative (1-2)' },
  ],
  replyStatus: [
    { value: 'needs_reply', label: 'Needs a reply' },
    { value: 'replied', label: 'Replied' },
  ],
  date: [],
};

export const REVIEW_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'rating_high', label: 'Highest rating' },
  { value: 'rating_low', label: 'Lowest rating' },
];

export function useReviewFilters() {
  const filterState = useLocalDashboardFilters(REVIEW_FILTER_CONFIGS);
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get('page')) || 1));
  const [sort, setSort] = useState<ReviewSort>(
    () => (searchParams.get('sort') as ReviewSort) || 'newest',
  );

  /**
   * The date filter is the shared daterange control, which resolves its own
   * presets ("Last 7 Days" and friends) into concrete from/to dates. The backend
   * therefore only ever sees a range and never has to agree with the client
   * about what a preset means.
   */
  const query = useMemo<ReviewQueryParams>(() => {
    const v = filterState.values;
    const replyRaw = isSelect(v.replyStatus) ? v.replyStatus[0] : undefined;
    const date = isDateRange(v.date) ? v.date : { from: '', to: '' };

    return {
      page,
      pageSize: PAGE_SIZE,
      ratings: isSelect(v.rating) ? v.rating.map(Number).filter(Boolean) : [],
      sentiments: isSelect(v.sentiment) ? (v.sentiment as Sentiment[]) : [],
      replyStatus: (replyRaw === 'needs_reply' || replyRaw === 'replied'
        ? replyRaw
        : 'all') as ReplyStatus,
      dateFrom: date.from || undefined,
      dateTo: date.to || undefined,
      sort,
    };
  }, [filterState.values, page, sort]);

  /**
   * Changing a filter or the sort sends the user back to page 1.
   *
   * Without it, narrowing the results while on page 4 of a set that now has one
   * page renders an empty list, which reads as "nothing matches" rather than
   * "you are past the end". Compared as a signature rather than handled in an
   * effect so it does not also fire on every page click.
   */
  const signature = useMemo(() => JSON.stringify({ ...query, page: undefined }), [query]);
  const [lastSignature, setLastSignature] = useState(signature);

  if (signature !== lastSignature) {
    setLastSignature(signature);
    if (page !== 1) setPage(1);
  }

  // Filters and sort live in the URL so a filtered view survives a refresh and
  // can be shared. `replace` so paging does not stack history entries.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const v = filterState.values;

    const put = (key: string, value: string) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };

    put('rating', isSelect(v.rating) ? v.rating.join(',') : '');
    put('sentiment', isSelect(v.sentiment) ? v.sentiment.join(',') : '');
    put('reply', isSelect(v.replyStatus) ? v.replyStatus.join(',') : '');
    const d = isDateRange(v.date) ? v.date : { from: '', to: '' };
    put('from', d.from);
    put('to', d.to);
    put('sort', sort === 'newest' ? '' : sort);
    put('page', page === 1 ? '' : String(page));

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filterState.values, sort, page, searchParams, setSearchParams]);

  const activeFilterCount = useMemo(() => {
    const v = filterState.values;
    let n = 0;
    if (isSelect(v.rating) && v.rating.length) n++;
    if (isSelect(v.sentiment) && v.sentiment.length) n++;
    if (isSelect(v.replyStatus) && v.replyStatus.length) n++;
    if (isDateRange(v.date) && (v.date.from || v.date.to)) n++;
    return n;
  }, [filterState.values]);

  const clearAll = () => {
    REVIEW_FILTER_CONFIGS.forEach((c) => filterState.clearFilter(c.key));
    setSort('newest');
    setPage(1);
  };

  return { filterState, query, sort, setSort, page, setPage, activeFilterCount, clearAll };
}

export type ReviewFilters = ReturnType<typeof useReviewFilters>;
